import { FC, memo, ReactElement, ReactNode, useCallback, useEffect, useState } from 'react';
import { IntlProvider } from 'react-intl';

import { getI18nMessages } from '../localization/getI18nMessages';
import { IntlError, logI18nMissingTranslation } from '../utils/logI18nMissingTranslation';

export type BaseIntlProviderProps = {
  locale: string;
  isProd: boolean;
  serviceIdentifier?: string;
  onLoadMessages?: () => void;
  awaitLoadMessages?: boolean;
  children: ReactNode;
  upstreamMessages?: Record<string, string>;
  /**
   * If not provided will use default
   */
  smartlingProjectId?: string;
  /**
   * Set to true to skip translations
   */
  skipFetchMessages?: boolean;
};

export const BaseIntlProvider: FC<BaseIntlProviderProps> = memo(function BaseReactIntlProvider({
  locale,
  isProd,
  serviceIdentifier,
  onLoadMessages,
  awaitLoadMessages = true,
  children,
  upstreamMessages,
  smartlingProjectId,
  skipFetchMessages = false,
}) {
  const [messages, setMessages] = useState<Record<string, string> | undefined>();

  const handleIntlError = useCallback(
    (error: IntlError) => {
      if (
        error.code === 'MISSING_TRANSLATION' &&
        messages &&
        Object.keys(messages).length > 0 // Do not report if the messages are empty
      ) {
        logI18nMissingTranslation(error);
      }
    },
    [messages],
  );

  useEffect(() => {
    if (skipFetchMessages) {
      setMessages({});
      return;
    }

    const loadMessages = async () => {
      const newMessages = await getI18nMessages(
        locale,
        isProd,
        serviceIdentifier,
        smartlingProjectId,
      );
      setMessages({ ...upstreamMessages, ...newMessages });
    };
    void loadMessages();
  }, [locale, isProd, serviceIdentifier, upstreamMessages, smartlingProjectId, skipFetchMessages]);

  useEffect(() => {
    if (messages) {
      onLoadMessages?.();
    }
  }, [messages, onLoadMessages]);

  if (skipFetchMessages) {
    return children as ReactElement;
  }

  if (!messages && awaitLoadMessages) {
    return null;
  }

  return (
    <IntlProvider locale={locale} messages={messages} onError={handleIntlError}>
      {children}
    </IntlProvider>
  );
});
